import React, { useState, useEffect } from "react";
import {
  FileText,
  Download,
  Calendar,
  TrendingUp,
  TrendingDown,
  DollarSign,
  BarChart3,
  PieChart,
  Activity,
} from "lucide-react";
import api from "@/services/api";

const ConsolidatedReports = () => {
  const [activeReport, setActiveReport] = useState("profit-loss");
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    date_from: new Date(new Date().getFullYear(), 0, 1)
      .toISOString()
      .split("T")[0],
    date_to: new Date().toISOString().split("T")[0],
    as_of_date: new Date().toISOString().split("T")[0],
    subsidiary: "",
  });

  const SUBSIDIARIES = [
    { value: "", label: "All Subsidiaries" },
    { value: "puffin_group", label: "Puffin Group Ltd" },
    { value: "buildwise", label: "Buildwise Construction Ltd" },
    { value: "laser_shield", label: "Laser Shield Ltd." },
    { value: "maz_logistics", label: "Maz Logistics Ltd." },
    { value: "puffin_energy", label: "Puffin Energy Ltd." },
    { value: "swingtide", label: "Swingtide Consulting Ltd" },
    { value: "puffin_agro", label: "Puffin Agro Allied" },
  ];

  useEffect(() => {
    fetchReport();
  }, [activeReport, filters]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      let endpoint = "";
      let params = {};

      switch (activeReport) {
        case "profit-loss":
          endpoint = "/finance-aggregation/reports/profit-loss";
          params = {
            date_from: filters.date_from,
            date_to: filters.date_to,
            ...(filters.subsidiary && { subsidiary: filters.subsidiary }),
          };
          break;
        case "balance-sheet":
          endpoint = "/finance-aggregation/reports/balance-sheet";
          params = {
            as_of_date: filters.as_of_date,
            ...(filters.subsidiary && { subsidiary: filters.subsidiary }),
          };
          break;
        case "cash-flow":
          endpoint = "/finance-aggregation/reports/cash-flow";
          params = {
            date_from: filters.date_from,
            date_to: filters.date_to,
            ...(filters.subsidiary && { subsidiary: filters.subsidiary }),
          };
          break;
        default:
          return;
      }

      const response = await api.get(endpoint, { params });

      const data = response.data;
      if (data.success) {
        setReportData(data.data);
      }
    } catch (error) {
      console.error("Error fetching report:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const renderProfitLoss = () => {
    if (!reportData || !reportData.revenue || !reportData.costOfGoodsSold)
      return null;

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-xl">
          <h2 className="text-2xl font-bold mb-2">Profit & Loss Statement</h2>
          <p className="text-blue-100">
            Period: {reportData.period.from} to {reportData.period.to}
          </p>
          <p className="text-blue-100">Subsidiary: {reportData.subsidiary}</p>
        </div>

        {/* Revenue Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <TrendingUp className="h-5 w-5 mr-2 text-green-600" />
            Revenue
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between py-2">
              <span className="text-gray-600">Sales Revenue</span>
              <span className="font-medium text-green-600">
                {formatCurrency(reportData.revenue.sales)}
              </span>
            </div>
            <div className="flex justify-between py-2 border-t border-gray-200 font-semibold">
              <span className="text-gray-900">Total Revenue</span>
              <span className="text-green-600">
                {formatCurrency(reportData.revenue.totalRevenue)}
              </span>
            </div>
          </div>
        </div>

        {/* COGS Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <TrendingDown className="h-5 w-5 mr-2 text-orange-600" />
            Cost of Goods Sold
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between py-2">
              <span className="text-gray-600">COGS</span>
              <span className="font-medium text-orange-600">
                ({formatCurrency(reportData.costOfGoodsSold.cogs)})
              </span>
            </div>
            <div className="flex justify-between py-2 border-t border-gray-200 font-semibold">
              <span className="text-gray-900">Total COGS</span>
              <span className="text-orange-600">
                ({formatCurrency(reportData.costOfGoodsSold.totalCOGS)})
              </span>
            </div>
          </div>
        </div>

        {/* Gross Profit */}
        <div className="bg-blue-50 rounded-xl border border-blue-200 p-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Gross Profit
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Margin: {reportData.grossProfitMargin}%
              </p>
            </div>
            <span className="text-3xl font-bold text-blue-600">
              {formatCurrency(reportData.grossProfit)}
            </span>
          </div>
        </div>
        {/* {JSON.stringify(reportData.operatingExpenses)}  */}
        {/* Operating Expenses */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <DollarSign className="h-5 w-5 mr-2 text-red-600" />
            Operating Expenses
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between py-2">
              <span className="text-gray-600">Purchase Requests</span>
              <span className="font-medium text-red-600">
                ({formatCurrency(reportData.operatingExpenses.purchaseRequests)}
                )
              </span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-gray-600">HR Expenses</span>
              <span className="font-medium text-red-600">
                ({formatCurrency(reportData.operatingExpenses.hrExpenses)})
              </span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-gray-600">Payroll</span>
              <span className="font-medium text-red-600">
                ({formatCurrency(reportData.operatingExpenses.payroll)})
              </span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-gray-600">Inventory Purchases</span>
              <span className="font-medium text-red-600">
                (
                {formatCurrency(
                  reportData.operatingExpenses.inventoryPurchases || 0,
                )}
                )
              </span>
            </div>
            <div className="flex justify-between py-2 border-t border-gray-200 font-semibold">
              <span className="text-gray-900">Total Operating Expenses</span>
              <span className="text-red-600">
                (
                {formatCurrency(
                  reportData.operatingExpenses.totalOperatingExpenses,
                )}
                )
              </span>
            </div>
          </div>
        </div>

        {/* Operating Income */}
        <div className="bg-purple-50 rounded-xl border border-purple-200 p-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Operating Income
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Margin: {reportData.operatingMargin}%
              </p>
            </div>
            <span className="text-3xl font-bold text-purple-600">
              {formatCurrency(reportData.operatingIncome)}
            </span>
          </div>
        </div>

        {/* Net Income */}
        <div className="bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl p-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-2xl font-bold">Net Income</h3>
              <p className="text-green-100 mt-1">
                Net Profit Margin: {reportData.netProfitMargin}%
              </p>
            </div>
            <span className="text-4xl font-bold">
              {formatCurrency(reportData.netIncome)}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderBalanceSheet = () => {
    if (!reportData || !reportData.assets || !reportData.assets.currentAssets)
      return null;

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-6 rounded-xl">
          <h2 className="text-2xl font-bold mb-2">Balance Sheet</h2>
          <p className="text-purple-100">As of: {reportData.asOfDate}</p>
          <p className="text-purple-100">Subsidiary: {reportData.subsidiary}</p>
        </div>

        {/* Assets */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <TrendingUp className="h-5 w-5 mr-2 text-green-600" />
            Assets
          </h3>
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Current Assets
              </h4>
              <div className="space-y-2 ml-4">
                <div className="flex justify-between py-1">
                  <span className="text-gray-600">Cash</span>
                  <span className="font-medium">
                    {formatCurrency(reportData.assets.currentAssets.cash)}
                  </span>
                </div>
                <div className="flex justify-between py-1">
                  <span className="text-gray-600">Accounts Receivable</span>
                  <span className="font-medium">
                    {formatCurrency(
                      reportData.assets.currentAssets.accountsReceivable,
                    )}
                  </span>
                </div>
                <div className="flex justify-between py-1">
                  <span className="text-gray-600">Inventory</span>
                  <span className="font-medium">
                    {formatCurrency(reportData.assets.currentAssets.inventory)}
                  </span>
                </div>
                <div className="flex justify-between py-1 border-t border-gray-200 font-semibold">
                  <span className="text-gray-900">Total Current Assets</span>
                  <span className="text-green-600">
                    {formatCurrency(
                      reportData.assets.currentAssets.totalCurrentAssets,
                    )}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex justify-between py-2 border-t-2 border-gray-300 font-bold text-lg">
              <span className="text-gray-900">Total Assets</span>
              <span className="text-green-600">
                {formatCurrency(reportData.assets.totalAssets)}
              </span>
            </div>
          </div>
        </div>

        {/* Liabilities */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <TrendingDown className="h-5 w-5 mr-2 text-red-600" />
            Liabilities
          </h3>
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Current Liabilities
              </h4>
              <div className="space-y-2 ml-4">
                <div className="flex justify-between py-1">
                  <span className="text-gray-600">Accounts Payable</span>
                  <span className="font-medium">
                    {formatCurrency(
                      reportData.liabilities.currentLiabilities.accountsPayable,
                    )}
                  </span>
                </div>
                <div className="flex justify-between py-1">
                  <span className="text-gray-600">Payroll Liabilities</span>
                  <span className="font-medium">
                    {formatCurrency(
                      reportData.liabilities.currentLiabilities
                        .payrollLiabilities,
                    )}
                  </span>
                </div>
                <div className="flex justify-between py-1 border-t border-gray-200 font-semibold">
                  <span className="text-gray-900">
                    Total Current Liabilities
                  </span>
                  <span className="text-red-600">
                    {formatCurrency(
                      reportData.liabilities.currentLiabilities
                        .totalCurrentLiabilities,
                    )}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex justify-between py-2 border-t-2 border-gray-300 font-bold text-lg">
              <span className="text-gray-900">Total Liabilities</span>
              <span className="text-red-600">
                {formatCurrency(reportData.liabilities.totalLiabilities)}
              </span>
            </div>
          </div>
        </div>

        {/* Equity */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <PieChart className="h-5 w-5 mr-2 text-blue-600" />
            Equity
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between py-2">
              <span className="text-gray-600">Retained Earnings</span>
              <span className="font-medium">
                {formatCurrency(reportData.equity.retainedEarnings)}
              </span>
            </div>
            <div className="flex justify-between py-2 border-t-2 border-gray-300 font-bold text-lg">
              <span className="text-gray-900">Total Equity</span>
              <span className="text-blue-600">
                {formatCurrency(reportData.equity.totalEquity)}
              </span>
            </div>
          </div>
        </div>

        {/* Total */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl p-6">
          <div className="flex justify-between items-center">
            <h3 className="text-2xl font-bold">Total Liabilities & Equity</h3>
            <span className="text-4xl font-bold">
              {formatCurrency(reportData.totalLiabilitiesAndEquity)}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderCashFlow = () => {
    if (!reportData || !reportData.operatingActivities) return null;

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white p-6 rounded-xl">
          <h2 className="text-2xl font-bold mb-2">Cash Flow Statement</h2>
          <p className="text-teal-100">
            Period: {reportData.period.from} to {reportData.period.to}
          </p>
          <p className="text-teal-100">Subsidiary: {reportData.subsidiary}</p>
        </div>

        {/* Operating Activities */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Activity className="h-5 w-5 mr-2 text-blue-600" />
            Operating Activities
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between py-2">
              <span className="text-gray-600">Cash from Sales</span>
              <span className="font-medium text-green-600">
                {formatCurrency(reportData.operatingActivities.cashFromSales)}
              </span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-gray-600">Cash Paid for Purchases</span>
              <span className="font-medium text-red-600">
                {formatCurrency(
                  reportData.operatingActivities.cashPaidPurchases,
                )}
              </span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-gray-600">Cash Paid for Expenses</span>
              <span className="font-medium text-red-600">
                {formatCurrency(
                  reportData.operatingActivities.cashPaidExpenses,
                )}
              </span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-gray-600">Cash Paid for Payroll</span>
              <span className="font-medium text-red-600">
                {formatCurrency(reportData.operatingActivities.cashPaidPayroll)}
              </span>
            </div>
            <div className="flex justify-between py-2 border-t-2 border-gray-300 font-bold text-lg">
              <span className="text-gray-900">Net Cash from Operations</span>
              <span
                className={
                  parseFloat(
                    reportData.operatingActivities.netCashFromOperations,
                  ) >= 0
                    ? "text-green-600"
                    : "text-red-600"
                }
              >
                {formatCurrency(
                  reportData.operatingActivities.netCashFromOperations,
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Investing Activities */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <BarChart3 className="h-5 w-5 mr-2 text-purple-600" />
            Investing Activities
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between py-2 border-t border-gray-200 font-semibold">
              <span className="text-gray-900">Net Cash from Investing</span>
              <span className="text-gray-600">
                {formatCurrency(
                  reportData.investingActivities.netCashFromInvesting,
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Financing Activities */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <DollarSign className="h-5 w-5 mr-2 text-orange-600" />
            Financing Activities
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between py-2 border-t border-gray-200 font-semibold">
              <span className="text-gray-900">Net Cash from Financing</span>
              <span className="text-gray-600">
                {formatCurrency(
                  reportData.financingActivities.netCashFromFinancing,
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Net Cash Flow */}
        <div
          className={`rounded-xl p-6 ${parseFloat(reportData.netCashFlow) >= 0 ? "bg-gradient-to-r from-green-600 to-green-700" : "bg-gradient-to-r from-red-600 to-red-700"} text-white`}
        >
          <div className="flex justify-between items-center">
            <h3 className="text-2xl font-bold">Net Cash Flow</h3>
            <span className="text-4xl font-bold">
              {formatCurrency(reportData.netCashFlow)}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Consolidated Reports
          </h1>
          <p className="text-gray-600 mt-1">
            Comprehensive financial statements and analysis
          </p>
        </div>
        <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2">
          <Download className="h-4 w-4" />
          <span>Export PDF</span>
        </button>
      </div>

      {/* Report Type Selector */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-2">
        <div className="flex space-x-2">
          <button
            onClick={() => setActiveReport("profit-loss")}
            className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors ${
              activeReport === "profit-loss"
                ? "bg-blue-600 text-white"
                : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            <FileText className="h-5 w-5 inline-block mr-2" />
            Profit & Loss
          </button>
          <button
            onClick={() => setActiveReport("balance-sheet")}
            className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors ${
              activeReport === "balance-sheet"
                ? "bg-blue-600 text-white"
                : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            <PieChart className="h-5 w-5 inline-block mr-2" />
            Balance Sheet
          </button>
          <button
            onClick={() => setActiveReport("cash-flow")}
            className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors ${
              activeReport === "cash-flow"
                ? "bg-blue-600 text-white"
                : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            <Activity className="h-5 w-5 inline-block mr-2" />
            Cash Flow
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {activeReport !== "balance-sheet" && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date From
                </label>
                <input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) =>
                    handleFilterChange("date_from", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date To
                </label>
                <input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) =>
                    handleFilterChange("date_to", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </>
          )}
          {activeReport === "balance-sheet" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                As of Date
              </label>
              <input
                type="date"
                value={filters.as_of_date}
                onChange={(e) =>
                  handleFilterChange("as_of_date", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subsidiary
            </label>
            <select
              value={filters.subsidiary}
              onChange={(e) => handleFilterChange("subsidiary", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {SUBSIDIARIES.map((sub) => (
                <option key={sub.value} value={sub.value}>
                  {sub.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Info Note for Subsidiary Filtering */}
      {filters.subsidiary && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> POS transaction data is not included when
            filtering by subsidiary, as POS transactions are not linked to
            specific subsidiaries in the system.
          </p>
        </div>
      )}

      {/* Report Content */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Generating report...</span>
        </div>
      ) : (
        <>
          {activeReport === "profit-loss" && renderProfitLoss()}
          {activeReport === "balance-sheet" && renderBalanceSheet()}
          {activeReport === "cash-flow" && renderCashFlow()}
        </>
      )}
    </div>
  );
};

export default ConsolidatedReports;
